import { useActionLogSender } from '@framework/action-log';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelOperation } from '@view-model/domain/view-model';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
  destinationFolder: Folder;
};

export const MoveViewModelConfirmModal: React.FC<Props> = ({
  isOpen,
  onClose,
  viewModel,
  destinationFolder,
}: Props) => {
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const actionLogSender = useActionLogSender();

  useEffect(() => {
    setConfirmed(false);
    setIsSubmitting(false);
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      await ViewModelOperation.move(viewModel.workspaceId, viewModel.id, destinationFolder.id, actionLogSender);
      toast.success('ビューモデルを移動しました。');
      onClose();
    } catch (e) {
      toast.error('ビューモデルの移動に失敗しました。時間をあけてもう一度試してみてください。');
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  }, [actionLogSender, confirmed, destinationFolder.id, onClose, viewModel.id, viewModel.workspaceId]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={onClose}
      title={'移動してもよろしいですか？'}
      submitButton={
        <Button color="primary" onClick={handleSubmit} disabled={!confirmed} loading={isSubmitting}>
          移動
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={onClose} disabled={isSubmitting}>
          キャンセル
        </Button>
      }
    >
      <div>
        <p>
          「{`${viewModel.name}`}」を
          {destinationFolder.isRoot() ? 'このワークスペース直下' : `「${destinationFolder.name}」配下`}へ移動しますか？
        </p>
        <label className="block">
          <input
            type="checkbox"
            className="form-checkbox mr-1 size-6 border-brand"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          移動するにはチェックしてください
        </label>
      </div>
    </GeneralModal>
  );
};
